export const DEFAULT_MOBILE_FOOTER = "games-six";

export const DEFAULT_FOOTER_BG =
  "linear-gradient(-180deg, #243a48 20%, #172732 91%)";

export const MOBILE_FOOTER_IDS = [
  "games-six",
  "default",
  "home-arc",
  "games-five",
] as const;

export type MobileFooterId = (typeof MOBILE_FOOTER_IDS)[number];

export type FooterNavKey =
  | "games"
  | "sports"
  | "inplay"
  | "home"
  | "multi"
  | "account";

export type FooterActiveStyle = "arch" | "arc-fill" | "rect" | "none";

export interface FooterLayout {
  items: FooterNavKey[];
  gamesFab: boolean;
  raised: "home" | "games" | "none";
  active: FooterActiveStyle;
}

export const FOOTER_LAYOUTS: Record<MobileFooterId, FooterLayout> = {
  "games-six": {
    items: ["games", "sports", "inplay", "home", "multi", "account"],
    gamesFab: false,
    raised: "games",
    active: "rect",
  },
  default: {
    items: ["sports", "inplay", "home", "multi", "account"],
    gamesFab: true,
    raised: "home",
    active: "arch",
  },
  "home-arc": {
    items: ["sports", "inplay", "home", "multi", "account"],
    gamesFab: true,
    raised: "home",
    active: "arc-fill",
  },
  "games-five": {
    items: ["games", "sports", "inplay", "home", "account"],
    gamesFab: false,
    raised: "games",
    active: "arc-fill",
  },
};

const FOOTER_CACHE_PREFIX = "sky-mobile-footer-v2:";

export const normalizeMobileFooterId = (value?: string | null): MobileFooterId => {
  const id = String(value || "").trim();
  if (!id) return DEFAULT_MOBILE_FOOTER;
  if (id === "games-fill" || id === "games-bar" || id === "games-six") return "games-six";
  if (id === "home-bump" || id === "home-raised" || id === "default") return "default";
  if (id === "home-arc" || id === "games-five") return id;
  return DEFAULT_MOBILE_FOOTER;
};

const parseHex = (color: string): [number, number, number] | null => {
  const match = String(color || "")
    .trim()
    .match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
};

export const lightenCssColor = (color: string, mixWhite = 0.32) => {
  const rgb = parseHex(color);
  if (!rgb) return "rgba(255,255,255,0.16)";
  const [r, g, b] = rgb.map((value) =>
    Math.round(value + (255 - value) * mixWhite)
  );
  return `rgb(${r}, ${g}, ${b})`;
};

export const DEFAULT_FOOTER_ACTIVE =
  "linear-gradient(-180deg, #32617f 20%, #1f4258 91%)";

export const getMobileFooterVars = (
  schema?: string,
  activeColor?: string
) => {
  const value = String(schema || "")
    .trim()
    .replace(/;+\s*$/, "");
  const activeValue = String(activeColor || "")
    .trim()
    .replace(/;+\s*$/, "");
  const bg = value || DEFAULT_FOOTER_BG;
  const isGradient = /gradient\s*\(/i.test(bg);
  const style: Record<string, string> = {
    "--mobile-footer-bg": bg,
  };

  const resolveActive = () => {
    if (activeValue) return activeValue;
    if (value && !isGradient && parseHex(value)) {
      return lightenCssColor(value, 0.34);
    }
    return DEFAULT_FOOTER_ACTIVE;
  };

  if (isGradient) {
    style.background = bg;
    style.backgroundImage = bg;
    style["--mobile-footer-bump"] = "#243a48";
    style["--mobile-footer-active"] = resolveActive();
    style["--mobile-footer-glow"] = "rgba(64, 196, 255, 0.55)";
    style["--mobile-footer-glow-soft"] = "rgba(64, 196, 255, 0.22)";
  } else {
    style.backgroundColor = bg;
    style.backgroundImage = "none";
    style["--mobile-footer-bump"] = value ? bg : "#243a48";
    style["--mobile-footer-active"] = resolveActive();
    const rgb = parseHex(activeValue || (value ? value : ""));
    if (rgb) {
      style["--mobile-footer-glow"] = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.55)`;
      style["--mobile-footer-glow-soft"] = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.22)`;
    } else {
      style["--mobile-footer-glow"] = "rgba(64, 196, 255, 0.55)";
      style["--mobile-footer-glow-soft"] = "rgba(64, 196, 255, 0.22)";
    }
  }
  return style;
};

export const footerCacheKey = (hostname = window.location.hostname) =>
  `${FOOTER_CACHE_PREFIX}${hostname}`;

export const readFooterCache = (): {
  mobileFooter: MobileFooterId;
  mobileFooterSchema: string;
  mobileFooterActiveColor: string;
} | null => {
  try {
    const raw = localStorage.getItem(footerCacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      mobileFooter: normalizeMobileFooterId(parsed?.mobileFooter),
      mobileFooterSchema: String(parsed?.mobileFooterSchema || ""),
      mobileFooterActiveColor: String(parsed?.mobileFooterActiveColor || ""),
    };
  } catch {
    return null;
  }
};

export const writeFooterCache = (
  mobileFooter: MobileFooterId,
  mobileFooterSchema: string,
  mobileFooterActiveColor = ""
) => {
  try {
    localStorage.setItem(
      footerCacheKey(),
      JSON.stringify({
        mobileFooter,
        mobileFooterSchema,
        mobileFooterActiveColor,
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

const FOOTER_VIEWPORT_MAX = 992;
const REPAINT_AFTER_HIDDEN_MS = 30 * 1000;

export const isMobileFooterView = () => {
  if (typeof window === "undefined") return false;
  const inner = window.innerWidth || 0;
  const screenW = window.screen?.width || 0;
  const visual = window.visualViewport?.width || 0;
  const widths = [inner, screenW, visual].filter((n) => n > 0);
  const minW = widths.length ? Math.min(...widths) : inner;
  if (minW <= FOOTER_VIEWPORT_MAX) return true;
  try {
    return window.matchMedia(`(max-width: ${FOOTER_VIEWPORT_MAX}px)`).matches;
  } catch {
    return false;
  }
};

export const syncMobileFooterViewportClass = () => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(
    "is-mobile-footer",
    isMobileFooterView()
  );
};

export const subscribeTabResume = (
  onResume: (info: { persisted: boolean; hiddenForMs: number }) => void
) => {
  let hiddenAt =
    typeof document !== "undefined" && document.visibilityState === "hidden"
      ? Date.now()
      : null;

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    const hiddenForMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    onResume({ persisted: false, hiddenForMs });
  };

  const onPageShow = (event: PageTransitionEvent) => {
    const hiddenForMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    onResume({ persisted: !!event.persisted, hiddenForMs });
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pageshow", onPageShow);
  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pageshow", onPageShow);
  };
};

export const shouldRepaintFooterOnResume = (info: {
  persisted: boolean;
  hiddenForMs: number;
}) => info.persisted || info.hiddenForMs >= REPAINT_AFTER_HIDDEN_MS;
